// @ts-nocheck
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/auth-context';
import {
  useAuditTrailPaginated,
  type AuditFilterUserOption,
  type AuditLogEntry,
  type AuditTrailFilterOptions,
  type AuditTrailFilters,
} from '@/hooks/api-hooks';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Shield,
  Search,
  Download,
  BarChart3,
  Filter,
  RefreshCw,
  AlertTriangle,
  Eye,
  Calendar,
  User,
  Activity,
  FileX,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Clock,
  Globe,
  UserCheck,
  UserX,
  Settings,
  Database,
  FileText,
  Plus,
  Trash2,
  Edit3,
  LogIn,
  LogOut,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Audit entry severity colors
const getSeverityColor = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
    case 'info':
      return 'outline';
    default:
      return 'outline';
  }
};

// Action type icons
const getActionIcon = (action: string) => {
  switch (action?.toLowerCase()) {
    case 'create':
    case 'created':
      return Plus;
    case 'update':
    case 'updated':
      return Edit3;
    case 'delete':
    case 'deleted':
      return Trash2;
    case 'login':
      return LogIn;
    case 'logout':
      return LogOut;
    case 'failed_login':
      return UserX;
    case 'system_backup':
      return Database;
    default:
      return Activity;
  }
};

const resolveAuditDescription = (entry: AuditLogEntry): string => {
  if (entry.description && entry.description.trim().length > 0) {
    return entry.description;
  }

  const action = entry.action?.replace(/_/g, ' ').toLowerCase() || 'performed an action on';
  const entity = entry.entityType?.replace(/_/g, ' ').toLowerCase() || 'record';
  const entityId = entry.entityId ? ` (${entry.entityId})` : '';

  return `${action} ${entity}${entityId}`.trim();
};

type AuditTrailStateFilters = AuditTrailFilters & {
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  user: string;
  action: string;
  entityType: string;
  severity: string;
  ipAddress: string;
  userAgent: string;
};

type PaginationToken = number | 'LEFT_ELLIPSIS' | 'RIGHT_ELLIPSIS';

function createPaginationRange(currentPage: number, totalPages: number, siblingCount = 1): PaginationToken[] {
  if (totalPages <= 1) {
    return [1];
  }

  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  const range: PaginationToken[] = [1];

  if (!showLeftEllipsis) {
    for (let page = 2; page < leftSiblingIndex; page += 1) {
      range.push(page);
    }
  } else {
    range.push('LEFT_ELLIPSIS');
  }

  for (let page = leftSiblingIndex; page <= rightSiblingIndex; page += 1) {
    range.push(page);
  }

  if (!showRightEllipsis) {
    for (let page = rightSiblingIndex + 1; page < totalPages; page += 1) {
      range.push(page);
    }
  } else {
    range.push('RIGHT_ELLIPSIS');
  }

  range.push(totalPages);

  return range;
}

// Debounce utility
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

function AuditMetricsDashboard({ auditEntries, isLoading }: { auditEntries: AuditLogEntry[]; isLoading: boolean }) {
  const metrics = useMemo(() => {
    const total = auditEntries.length;
    const critical = auditEntries.filter(entry => entry.severity === 'critical').length;
    const high = auditEntries.filter(entry => entry.severity === 'high').length;
    const medium = auditEntries.filter(entry => entry.severity === 'medium').length;
    const low = auditEntries.filter(entry => entry.severity === 'low').length;
    const info = auditEntries.filter(entry => entry.severity === 'info').length;

    const today = auditEntries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      const todayDate = new Date();
      return entryDate.toDateString() === todayDate.toDateString();
    }).length;

    const thisWeek = auditEntries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return entryDate > weekAgo;
    }).length;

    const failedLogins = auditEntries.filter(entry => entry.action === 'FAILED_LOGIN').length;
    const logins = auditEntries.filter(entry => entry.action === 'LOGIN').length;
    const creates = auditEntries.filter(entry => entry.action === 'CREATE').length;
    const updates = auditEntries.filter(entry => entry.action === 'UPDATE').length;
    const deletes = auditEntries.filter(entry => entry.action === 'DELETE').length;

    return {
      total, critical, high, medium, low, info,
      today, thisWeek, failedLogins, logins,
      creates, updates, deletes
    };
  }, [auditEntries]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Events</p>
              <p className="text-2xl font-bold">{metrics.total}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Critical</p>
              <p className="text-2xl font-bold text-red-600">{metrics.critical}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Today</p>
              <p className="text-2xl font-bold">{metrics.today}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <UserX className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Failed Logins</p>
              <p className="text-2xl font-bold text-orange-600">{metrics.failedLogins}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Successful Logins</p>
              <p className="text-2xl font-bold">{metrics.logins}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditFiltersPanel({ filters, onFiltersChange, auditEntries, availableFilters }: {
  filters: AuditTrailFilters;
  onFiltersChange: (filters: Partial<AuditTrailFilters>) => void;
  auditEntries: AuditLogEntry[];
  availableFilters?: AuditTrailFilterOptions;
}) {
  const uniqueUsers = useMemo<AuditFilterUserOption[]>(() => {
    if (availableFilters?.users?.length) {
      return [...availableFilters.users].sort((a, b) => a.name.localeCompare(b.name));
    }

    const users = auditEntries
      .filter((entry) => Boolean(entry.userId))
      .map((entry) => ({
        id: entry.userId as string,
        name: entry.userName,
        email: entry.userEmail ?? null,
      }))

    return Array.from(new Map(users.map((user) => [user.id, user])).values())
      .map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableFilters?.users, auditEntries]);

  const uniqueActions = useMemo(() => {
    if (availableFilters?.actions?.length) {
      return [...availableFilters.actions].sort((a, b) => a.localeCompare(b));
    }
    return ([...new Set(auditEntries.map(entry => entry.action?.toUpperCase()).filter(Boolean))] as string[])
      .sort((a, b) => a.localeCompare(b));
  }, [availableFilters?.actions, auditEntries]);

  const uniqueEntityTypes = useMemo(() => {
    if (availableFilters?.entityTypes?.length) {
      return [...availableFilters.entityTypes].sort((a, b) => a.localeCompare(b));
    }
    return ([...new Set(auditEntries.map(entry => entry.entityType?.toUpperCase()).filter(Boolean))] as string[])
      .sort((a, b) => a.localeCompare(b));
  }, [availableFilters?.entityTypes, auditEntries]);

  const uniqueSeverities = useMemo(() => {
    const severityOrder = ['critical', 'high', 'medium', 'low', 'info', 'warn', 'error', 'debug'];
    const sortBySeverity = (a: string, b: string) => {
      const indexA = severityOrder.indexOf(a.toLowerCase());
      const indexB = severityOrder.indexOf(b.toLowerCase());
      const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      if (safeA === safeB) {
        return a.localeCompare(b);
      }
      return safeA - safeB;
    };

    if (availableFilters?.severities?.length) {
      return [...availableFilters.severities].sort(sortBySeverity);
    }
    return [...new Set(auditEntries.map(entry => entry.severity))].sort(sortBySeverity);
  }, [availableFilters?.severities, auditEntries]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <label className="text-sm font-medium mb-2 block">User</label>
        <Select value={filters.user} onValueChange={(value) => onFiltersChange({ user: value })}>
          <SelectTrigger>
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {uniqueUsers.map(user => (
              <SelectItem key={user.id} value={user.id}>
                {user.name}
                {user.email ? ` (${user.email})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Action</label>
        <Select value={filters.action} onValueChange={(value) => onFiltersChange({ action: value })}>
          <SelectTrigger>
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {uniqueActions.map(action => (
              <SelectItem key={action} value={action}>
                {action.replace(/_/g, ' ').toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Entity Type</label>
        <Select value={filters.entityType} onValueChange={(value) => onFiltersChange({ entityType: value })}>
          <SelectTrigger>
            <SelectValue placeholder="All entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {uniqueEntityTypes.map(entityType => (
              <SelectItem key={entityType} value={entityType}>
                {entityType.toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Severity</label>
        <Select value={filters.severity} onValueChange={(value) => onFiltersChange({ severity: value })}>
          <SelectTrigger>
            <SelectValue placeholder="All severities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {uniqueSeverities.map(severity => (
              <SelectItem key={severity} value={severity}>
                {severity.charAt(0).toUpperCase() + severity.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function AuditTrailTable({
  auditEntries,
  isLoading,
  onEntrySelect,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange
}: {
  auditEntries: AuditLogEntry[];
  isLoading: boolean;
  onEntrySelect: (entry: AuditLogEntry) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}) {
  const paginationRange = useMemo(
    () => createPaginationRange(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (auditEntries.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="mx-auto w-20 h-20 bg-muted/50 rounded-full flex items-center justify-center mb-4">
          <FileText className="w-10 h-10 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No audit entries found</h3>
        <p className="text-muted-foreground">Try adjusting your search criteria or filters.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="divide-y">
        {auditEntries.map((entry) => {
          const ActionIcon = getActionIcon(entry.action);
          const description = resolveAuditDescription(entry);
          return (
            <div
              key={entry.id}
              className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => onEntrySelect(entry)}
            >
              <div className="flex items-start gap-4">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                  entry.severity === 'critical' ? 'bg-red-100 text-red-600' :
                  entry.severity === 'high' ? 'bg-orange-100 text-orange-600' :
                  entry.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                  entry.severity === 'low' ? 'bg-blue-100 text-blue-600' :
                  'bg-gray-100 text-gray-600'
                )}>
                  <ActionIcon className="h-5 w-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-medium">{entry.userName}</h4>
                    <Badge variant={getSeverityColor(entry.severity)} className="text-xs">
                      {entry.severity}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {entry.action.replace(/_/g, ' ').toLowerCase()} {entry.entityType?.toLowerCase()}
                    {entry.entityId && `(${entry.entityId})`}
                  </p>

                  <p className="text-sm">{description}</p>

                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    {entry.ipAddress && (
                      <div className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {entry.ipAddress}
                      </div>
                    )}
                    {entry.location && (
                      <span>{entry.location}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <div className="flex items-center gap-1">
                {paginationRange.map((item, index) => {
                  if (item === 'LEFT_ELLIPSIS' || item === 'RIGHT_ELLIPSIS') {
                    return (
                      <span key={`${item}-${index}`} className="px-2 text-xs text-muted-foreground">
                        …
                      </span>
                    );
                  }

                  return (
                    <Button
                      key={item}
                      variant={currentPage === item ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageChange(item)}
                      className="w-8 h-8 p-0"
                    >
                      {item}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditEntryDetailModal({ entry, open, onOpenChange }: {
  entry: AuditLogEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!entry) return null;

  const ActionIcon = getActionIcon(entry.action);
  const description = resolveAuditDescription(entry);
  const additionalDetails = entry.details ?? entry.metadata;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              entry.severity === 'critical' ? 'bg-red-100 text-red-600' :
              entry.severity === 'high' ? 'bg-orange-100 text-orange-600' :
              entry.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
              entry.severity === 'low' ? 'bg-blue-100 text-blue-600' :
              'bg-gray-100 text-gray-600'
            )}>
              <ActionIcon className="h-5 w-5" />
            </div>
            Audit Entry Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">User</label>
              <p className="text-sm font-medium">{entry.userName}</p>
              <p className="text-xs text-muted-foreground">{entry.userEmail}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
              <p className="text-sm">{new Date(entry.timestamp).toLocaleString()}</p>
            </div>
          </div>

          {/* Action Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Action</label>
              <p className="text-sm">{entry.action.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Severity</label>
              <Badge variant={getSeverityColor(entry.severity)}>
                {entry.severity}
              </Badge>
            </div>
          </div>

          {/* Entity Details */}
          {(entry.entityType || entry.entityId) && (
            <div className="grid grid-cols-2 gap-4">
              {entry.entityType && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Entity Type</label>
                  <p className="text-sm">{entry.entityType}</p>
                </div>
              )}
              {entry.entityId && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Entity ID</label>
                  <p className="text-sm font-mono">{entry.entityId}</p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Description</label>
            <p className="text-sm bg-muted p-3 rounded-lg mt-1">{description}</p>
          </div>

          {/* Technical Details */}
          <div className="grid grid-cols-1 gap-4">
            {entry.ipAddress && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">IP Address</label>
                <p className="text-sm font-mono">{entry.ipAddress}</p>
              </div>
            )}
            {entry.userAgent && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">User Agent</label>
                <p className="text-xs bg-muted p-2 rounded font-mono break-all">{entry.userAgent}</p>
              </div>
            )}
            {entry.location && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Location</label>
                <p className="text-sm">{entry.location}</p>
              </div>
            )}
          </div>

          {/* Additional Details */}
          {additionalDetails && Object.keys(additionalDetails).length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Additional Details</label>
              <pre className="text-xs bg-muted p-3 rounded-lg mt-1 overflow-x-auto">
                {JSON.stringify(additionalDetails, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AuditExportModal({ open, onOpenChange, auditEntries }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditEntries: AuditLogEntry[];
}) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      if (exportFormat === 'csv') {
        const csvContent = [
          // Header row
          ['Timestamp', 'User', 'Email', 'Action', 'Entity Type', 'Entity ID', 'Severity', 'Description', 'IP Address', 'Location'].join(','),
          // Data rows
          ...auditEntries.map(entry => {
            const description = resolveAuditDescription(entry);
            return [
            entry.timestamp,
            `"${entry.userName}"`,
            entry.userEmail,
            entry.action,
            entry.entityType || '',
            entry.entityId || '',
            entry.severity,
            `"${description}"`,
            entry.ipAddress || '',
            entry.location || ''
          ].join(',')
          })
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const jsonContent = JSON.stringify(auditEntries, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast.success('Audit trail exported successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to export audit trail');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Audit Trail</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Export Format</label>
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV (Comma Separated)</SelectItem>
                <SelectItem value="json">JSON (JavaScript Object)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Exporting {auditEntries.length} audit entries with current filters applied.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting}
              className="flex-1"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AuditTrailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Permission check - only OWNER/ADMIN can access audit trail
  const userRole = user?.role || user?.organizationRole;
  const normalizedRole = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  const canAccessAuditTrail = normalizedRole === 'OWNER' || normalizedRole === 'ADMIN';

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showMetrics, setShowMetrics] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100); // 100 items per page as requested

  // Filters state
  const [filters, setFilters] = useState<AuditTrailStateFilters>({
    dateRange: { from: null, to: null },
    user: 'all',
    action: 'all',
    entityType: 'all',
    severity: 'all',
    ipAddress: '',
    userAgent: ''
  });

  // Debounced search
  const debouncedSearchFn = useCallback(
    debounce((value: string) => setDebouncedSearch(value), 300),
    []
  );

  useEffect(() => {
    debouncedSearchFn(searchQuery);
  }, [searchQuery, debouncedSearchFn]);

  // API hook for audit trail data with pagination
  const {
    data: auditData,
    isLoading,
    error,
    refetch: refetchAuditTrail
  } = useAuditTrailPaginated(currentPage, itemsPerPage, {
    search: debouncedSearch,
    ...filters,
    enabled: canAccessAuditTrail
  });

  // Transform and filter audit entries
  const auditEntries = useMemo<AuditLogEntry[]>(() => {
    if (!auditData?.data) return [];
    return auditData.data;
  }, [auditData]);

  // Extract pagination info
  const pagination = auditData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: itemsPerPage,
    hasMore: false,
  };

  const availableFilters = auditData?.filters;
  const totalEntries = pagination.totalItems ?? auditEntries.length;

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = pagination.totalItems; // Use total items from pagination
    const critical = auditEntries.filter(entry => entry.severity === 'critical').length;
    const high = auditEntries.filter(entry => entry.severity === 'high').length;
    const today = auditEntries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      const todayDate = new Date();
      return entryDate.toDateString() === todayDate.toDateString();
    }).length;
    const thisWeek = auditEntries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return entryDate > weekAgo;
    }).length;

    return { total, critical, high, today, thisWeek };
  }, [auditEntries, pagination.totalItems]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<AuditTrailFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Handle entry selection
  const handleEntrySelect = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
    setDetailModalOpen(true);
  };

  // Handle export
  const handleExport = () => {
    setExportModalOpen(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchAuditTrail();
    toast.success("Audit trail refreshed");
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  // Show access denied for non-admin users
  if (!canAccessAuditTrail) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md mx-auto">
          <div className="mx-auto w-24 h-24 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <Shield className="w-12 h-12 text-destructive" />
          </div>
          <div className="text-6xl font-bold text-muted-foreground mb-4">403</div>
          <h2 className="text-2xl font-bold mb-4">Access Forbidden</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the audit trail. This feature is only available to organization owners and administrators.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to Dashboard
            </Button>
            <Button onClick={() => navigate(-1)} variant="outline" className="w-full">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-background min-h-screen">
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground"></div>
            <span className="text-muted-foreground">Loading audit trail...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-background min-h-screen">
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md mx-auto">
            <div className="mx-auto w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-destructive" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Failed to Load Audit Trail</h3>
            <p className="text-muted-foreground mb-6">
              {error.message || 'An unexpected error occurred while loading the audit trail.'}
            </p>
            <Button onClick={() => refetchAuditTrail()} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Audit Trail</h1>
                <p className="text-muted-foreground">
                  Security and activity monitoring for your organization
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Activity className="h-4 w-4" />
                    <span>{metrics.total} total entries</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{metrics.today} today</span>
                  </div>
                  {metrics.critical > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {metrics.critical} critical
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setShowMetrics(!showMetrics)}
                className="gap-2"
                aria-label={showMetrics ? 'Hide metrics dashboard' : 'Show metrics dashboard'}
              >
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">{showMetrics ? 'Hide' : 'Show'} Metrics</span>
                <span className="sm:hidden">{showMetrics ? 'Hide' : 'Show'}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
                aria-label={showFilters ? 'Hide filters panel' : 'Show filters panel'}
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">{showFilters ? 'Hide' : 'Show'} Filters</span>
                <span className="sm:hidden">{showFilters ? 'Hide' : 'Show'}</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                className="gap-2"
                aria-label="Export audit entries"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                className="gap-2"
                aria-label="Refresh audit trail"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Dashboard */}
        {showMetrics && (
          <AuditMetricsDashboard
            auditEntries={auditEntries}
            isLoading={isLoading}
          />
        )}

        {/* Search and Filters */}
        <Card>
          <CardContent className="p-6">
            {/* Search Bar */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search audit entries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11"
                    aria-label="Search audit entries"
                    role="searchbox"
                  />
                </div>
                <Badge variant="outline" className="px-3 py-1">
                  {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
                </Badge>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <>
                  <Separator />
                  <AuditFiltersPanel
                    filters={filters}
                    onFiltersChange={handleFilterChange}
                    auditEntries={auditEntries}
                    availableFilters={availableFilters}
                  />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Audit Trail Table */}
        <Card>
          <CardContent className="p-0">
            <AuditTrailTable
              auditEntries={auditEntries}
              isLoading={isLoading}
              onEntrySelect={handleEntrySelect}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
            />
          </CardContent>
        </Card>

      </div>

      {/* Modals */}
      <AuditEntryDetailModal
        entry={selectedEntry}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />

      <AuditExportModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        auditEntries={auditEntries}
      />
    </div>
  );
}
